import React, { useEffect, useState, useCallback } from 'react';

/**
 * Amenity Stock (docs/DEV_PMS_OPERATIONS_ENHANCEMENT.md Module 1) — day-to-day
 * storekeeper screen: post a purchase (stock in), post a manual adjustment
 * (signed delta — wastage/damage/found stock/manual issue), and see current
 * on-hand balance per item. Room-consumption postings from the housekeeping
 * app show up in this same balance automatically; they aren't entered here.
 */
function HkAmenityStock({ token, selectedProperty }) {
  const [items, setItems] = useState([]);
  const [balance, setBalance] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [mode, setMode] = useState('purchase'); // 'purchase' | 'adjustment'
  const [form, setForm] = useState({ item_id: '', quantity: '', unit_cost: '', quantity_delta: '', remarks: '' });
  const [posting, setPosting] = useState(false);

  const headers = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || '',
  }), [token, selectedProperty]);

  const load = useCallback(async () => {
    if (!token || !selectedProperty?.id) return;
    setLoading(true); setError('');
    try {
      const [itemsRes, balanceRes] = await Promise.all([
        fetch('/api/v1/amenity-inventory/items', { headers: headers() }),
        fetch('/api/v1/amenity-inventory/stock/balance', { headers: headers() }),
      ]);
      const itemsData = await itemsRes.json().catch(() => ([]));
      const balanceData = await balanceRes.json().catch(() => ([]));
      if (!itemsRes.ok) { setError(itemsData.error || 'Failed to load items'); return; }
      setItems(itemsData);
      setBalance(balanceRes.ok ? balanceData : []);
    } catch (err) {
      setError(err?.message || 'Network error');
    } finally { setLoading(false); }
  }, [token, selectedProperty, headers]);

  useEffect(() => { load(); }, [load]);

  const post = async () => {
    setError(''); setSuccess(''); setPosting(true);
    try {
      const endpoint = mode === 'purchase' ? '/api/v1/amenity-inventory/stock/purchase' : '/api/v1/amenity-inventory/stock/adjustment';
      const body = mode === 'purchase'
        ? { item_id: Number(form.item_id), quantity: Number(form.quantity), unit_cost: Number(form.unit_cost || 0), remarks: form.remarks }
        : { item_id: Number(form.item_id), quantity_delta: Number(form.quantity_delta), remarks: form.remarks };
      const res = await fetch(endpoint, { method: 'POST', headers: headers(), body: JSON.stringify(body) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setError(data.error || 'Failed to post'); return; }
      setSuccess(mode === 'purchase' ? 'Purchase posted.' : 'Adjustment posted.');
      setForm({ item_id: '', quantity: '', unit_cost: '', quantity_delta: '', remarks: '' });
      load();
    } catch (err) {
      setError(err?.message || 'Network error');
    } finally { setPosting(false); }
  };

  if (!selectedProperty?.id) {
    return <div style={{ padding: 24, color: '#6b7280' }}>Select a property to manage amenity stock.</div>;
  }

  return (
    <div className="page-shell" style={{ maxWidth: 1080, margin: '0 auto' }}>
      <h2 style={{ marginBottom: 4 }}>Amenity Stock</h2>
      <p style={{ color: '#666', fontSize: 14, marginBottom: 16 }}>
        Post purchases and adjustments here. Room-cleaning consumption posts automatically from the housekeeping app.
      </p>

      {error && <div style={{ background: '#fde8e8', color: '#b71c1c', padding: '8px 12px', borderRadius: 4, marginBottom: 12, fontSize: 14 }}>{error}</div>}
      {success && <div style={{ background: '#e8f5e9', color: '#2e7d32', padding: '8px 12px', borderRadius: 4, marginBottom: 12, fontSize: 14 }}>{success}</div>}

      <div style={{ padding: 16, background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 8, marginBottom: 24, maxWidth: 480 }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <button onClick={() => setMode('purchase')}
            style={{ flex: 1, padding: '7px', borderRadius: 6, border: '1px solid #cbd5e1', cursor: 'pointer', fontWeight: 700, background: mode === 'purchase' ? '#1976d2' : '#fff', color: mode === 'purchase' ? '#fff' : '#1e293b' }}>
            Purchase (Stock In)
          </button>
          <button onClick={() => setMode('adjustment')}
            style={{ flex: 1, padding: '7px', borderRadius: 6, border: '1px solid #cbd5e1', cursor: 'pointer', fontWeight: 700, background: mode === 'adjustment' ? '#1976d2' : '#fff', color: mode === 'adjustment' ? '#fff' : '#1e293b' }}>
            Adjustment (± )
          </button>
        </div>

        <label style={labelStyle}>Item</label>
        <select value={form.item_id} onChange={e => setForm({ ...form, item_id: e.target.value })} style={{ ...inputStyle, marginBottom: 10 }}>
          <option value="">— select item —</option>
          {items.filter(i => i.is_active).map(i => <option key={i.item_id} value={i.item_id}>{i.item_name} ({i.unit})</option>)}
        </select>

        {mode === 'purchase' ? (
          <>
            <label style={labelStyle}>Quantity</label>
            <input type="number" min="0" value={form.quantity} onChange={e => setForm({ ...form, quantity: e.target.value })} style={{ ...inputStyle, marginBottom: 10 }} />
            <label style={labelStyle}>Unit Cost</label>
            <input type="number" min="0" value={form.unit_cost} onChange={e => setForm({ ...form, unit_cost: e.target.value })} style={{ ...inputStyle, marginBottom: 10 }} />
          </>
        ) : (
          <>
            <label style={labelStyle}>Quantity Delta (negative for wastage/damage/manual issue)</label>
            <input type="number" value={form.quantity_delta} onChange={e => setForm({ ...form, quantity_delta: e.target.value })} style={{ ...inputStyle, marginBottom: 10 }} />
          </>
        )}

        <label style={labelStyle}>Remarks</label>
        <input value={form.remarks} onChange={e => setForm({ ...form, remarks: e.target.value })} style={{ ...inputStyle, marginBottom: 12 }} />

        <button onClick={post} disabled={posting || !form.item_id}
          style={{ width: '100%', padding: '9px', background: posting || !form.item_id ? '#999' : '#1976d2', color: '#fff', border: 'none', borderRadius: 4, cursor: posting || !form.item_id ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 600 }}>
          {posting ? 'Posting…' : `Post ${mode === 'purchase' ? 'Purchase' : 'Adjustment'}`}
        </button>
      </div>

      <h3 style={{ marginBottom: 8 }}>On-Hand Balance</h3>
      {loading ? <p>Loading…</p> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
            <thead>
              <tr style={{ background: '#f5f5f5' }}>
                <th style={thStyle}>Item</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>On Hand</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Reorder Level</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Avg Cost</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Stock Value</th>
                <th style={thStyle}>Last Stockout</th>
              </tr>
            </thead>
            <tbody>
              {balance.map(b => {
                const low = b.reorder_level > 0 && Number(b.on_hand_qty) <= Number(b.reorder_level);
                return (
                  <tr key={b.item_id} style={{ background: low ? '#fff7ed' : 'transparent' }}>
                    <td style={tdStyle}>{b.item_name} <span style={{ color: '#94a3b8', fontSize: 12 }}>({b.unit})</span></td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, color: low ? '#c2410c' : '#1e293b' }}>{Number(b.on_hand_qty).toLocaleString()}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{Number(b.reorder_level).toLocaleString()}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{Number(b.weighted_avg_cost).toFixed(2)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{Number(b.stock_value).toFixed(2)}</td>
                    <td style={tdStyle}>
                      {b.last_stockout_at
                        ? <span style={{ color: '#b91c1c', fontWeight: 600 }} title="An auto-consumed or posted quantity exceeded on-hand stock at this time">⚠ {new Date(b.last_stockout_at).toLocaleString()}</span>
                        : <span style={{ color: '#cbd5e1' }}>—</span>}
                    </td>
                  </tr>
                );
              })}
              {!balance.length && (
                <tr><td colSpan={6} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No stock movements posted yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const labelStyle = { display: 'block', fontSize: 12.5, fontWeight: 600, color: '#475569', marginBottom: 4 };
const thStyle = { padding: '10px 8px', textAlign: 'left', borderBottom: '2px solid #ddd', whiteSpace: 'nowrap' };
const tdStyle = { padding: '8px', borderBottom: '1px solid #e0e0e0' };
const inputStyle = { width: '100%', padding: '6px 8px', border: '1px solid #ccc', borderRadius: 4, fontSize: 13.5, boxSizing: 'border-box' };

export default HkAmenityStock;
